import React, { useState, useEffect, useContext } from 'react';
import { apiPost, apiGet } from '../../utils/apiFetch';
import apiPath from '../../utils/pathObj';
import { useForm } from 'react-hook-form';
import useToastContext from 'hooks/useToastContext';
import OInputField from 'components/reusable/OInputField';
import { useTranslation } from 'react-i18next';
import { preventMaxInput } from 'utils/validations';
import formValidation from '../../utils/formValidation';
import Select from 'react-select';
import ErrorMessage from 'components/ErrorMessage';
import OTextArea from 'components/reusable/OTextArea';
import AuthContext from 'context/AuthContext';
import { Editor } from "@tinymce/tinymce-react";
import helpers from '../../utils/helpers';

const ManageAdd = ({ allJobs, handleCategory, item }) => {
  const { t } = useTranslation();
  const notification = useToastContext();
  const { user, fetchCompanyCompletionStatus } = useContext(AuthContext);
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    clearErrors,
    watch
  } = useForm({
    mode: 'onChange',
    shouldFocusError: true,
    defaultValues: {}
  });

  const [countryList, setCountryList] = useState([]);
  const [cityList, setCityList] = useState([]);
  const [cat, setCat] = useState(item?.Category?._id);
  const [subCat, setSubCat] = useState(item?.SubCategory?._id);
  const [isCatError, setIsCatError] = useState(false);
  const [isSubCatError, setIsSubCatError] = useState(false);
  const [country, setCountry] = useState(item?.Country?._id);
  const [city, setCity] = useState(item?.city);
  const [isCountryError, setIsCountryError] = useState(false);
  const [isCityError, setIsCityError] = useState(false);
  const [categoryList, setCategoryList] = useState([]);
  const [subCategoryList, setSubCategoryList] = useState([]);
  const [department, setDepartment] = useState(item?.Department?._id ?
    { label: item?.Department?.name, value: item?.Department?._id } : null);
  const [isDepartmentError, setIsDepartmentError] = useState(false);
  const [departmentList, setDepartmentList] = useState([]);
  const [skillList, setSkillList] = useState([]);
  const [referenceLink, setReferenceLink] = useState(item?.Skills ?
    item.Skills.map(skill => ({ label: skill.name, value: skill._id })) : []);
  const [isReferenceError, setIsReferenceError] = useState(false);
  const [skill, setSkill] = useState([]);
  const [companyData] = useState(user?._id);
  const [state, setState] = useState(item?.State?._id);
  const [stateList, setStateList] = useState([]);
  const [isStateError, setIsStateError] = useState(false);
  const [description, setDescription] = useState(item?.description || '');
  const [terminationCondition, setTerminationCondition] = useState(item?.terminationCondition || '');

  // Broadbean budget/duration — OPTIONAL
  const [budget, setBudget] = useState(item?.budget || '');
  const [duration, setDuration] = useState(item?.duration || '');
  const BUDGET_OPTIONS = [300, 500, 750];
  const DURATION_OPTIONS = [7, 14, 30];

  // 🆕 Saving state for the Add button spinner
  const [isSaving, setIsSaving] = useState(false);

  // AI Generation States
  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);
  const [isGeneratingTermination, setIsGeneratingTermination] = useState(false);
  const [aiStatus, setAiStatus] = useState('');

  // Watch form values
  const formValues = watch();
  const jobTitle = watch('name');
  const employmentLength = watch('employmentLength');
  const [currentLedgerAmount, setCurrentLedgerAmount] = useState(0);
const [billingCycle, setBillingCycle] = useState({ start: null, end: null });
const [isLoadingLedger, setIsLoadingLedger] = useState(false);

  const generateWithAI = async (type) => {
    if (!jobTitle || jobTitle.trim() === '') {
      notification.error('Please enter job title first');
      return;
    }

    if (type === 'description') {
      setIsGeneratingDescription(true);
      setAiStatus('Generating professional job description...');
    } else {
      setIsGeneratingTermination(true);
      setAiStatus('Generating termination conditions...');
    }

    try {
      const jobData = {
        employmentLength: employmentLength || '',
        department: department?.label || ''
      };

      const response = await apiPost(apiPath.generateAIContent, {
        contentType: type,
        jobTitle: jobTitle.trim(),
        jobData
      });

      if (response.data.success) {
        if (type === 'description') {
          setDescription(response.data.results.description);
          notification.success('AI job description generated successfully!');
        } else {
          setTerminationCondition(response.data.results.termination);
          notification.success('AI termination conditions generated successfully!');
        }
      } else {
        notification.error(response.data.message || `Failed to generate ${type}`);
      }
    } catch (error) {
      console.error('AI Generation Error:', error);
      notification.error(`Failed to generate ${type}. Please try again.`);
    } finally {
      setIsGeneratingDescription(false);
      setIsGeneratingTermination(false);
      setAiStatus('');
    }
  };

  const checkFields = () => {
    let isValid = true;
    if (!jobTitle || jobTitle.trim() === '') {
      isValid = false;
    }
    return isValid;
  };

  const calculateProRatedPreview = (budgetValue, durationValue) => {
  const budget = Number(budgetValue);
  const duration = Number(durationValue);
  if (!budget || !duration) return null;

  const now = new Date();
  const cycleStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const cycleEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);

  const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
  const cycleStartD = startOfDay(cycleStart);
  const cycleEndD = startOfDay(cycleEnd);
  const jobStartD = startOfDay(now);

  // 🆕 For a NEW job being added right now, the job runs for `duration`
  // days starting today. Its billable window ends at
  //   min(cycleEnd, jobStart + duration - 1)
  // not at "today". This is the key difference vs. Edit — the job
  // hasn't started accumulating days yet, it will run going forward.
  const jobEndD = new Date(jobStartD);
  jobEndD.setDate(jobEndD.getDate() + duration - 1);

  const overlapStart = new Date(Math.max(cycleStartD.getTime(), jobStartD.getTime()));
  const overlapEnd = new Date(Math.min(cycleEndD.getTime(), jobEndD.getTime()));

  const MS = 86400000;
  let daysThisCycle = 0;
  if (overlapEnd >= overlapStart) {
    daysThisCycle = Math.floor((overlapEnd - overlapStart) / MS) + 1;
  }
  const daysNextCycle = Math.max(0, duration - daysThisCycle);
  const dailyRate = budget / duration;
  const round2 = (n) => Math.round(n * 100) / 100;

  return {
    daysThisCycle,
    daysNextCycle,
    dailyRate: round2(dailyRate),
    amountThisCycle: round2(dailyRate * daysThisCycle),
    amountNextCycle: round2(dailyRate * daysNextCycle),
  };
};

  // ✅ Actual API call — with isSaving flag
  const submitJob = async (data) => {
    try {
      if (!data.name || data.name.trim() === '') {
        notification.error('Job title is required');
        return;
      }

      if (!description || description.trim() === '') {
        notification.error('Please add job description');
        return;
      }

      setIsSaving(true); // 🆕 start spinner

      let link = [];
      if (referenceLink.length > 0) {
        link = referenceLink.map(linkItem => linkItem.value);
      }

      // Budget/duration are OPTIONAL — send only if both selected.
      // Send null otherwise so the backend does not bill.
      const hasBudgetDuration = budget && duration;

      const payload = {
        name: data.name.trim(),
        companyID: companyData,
        countryID: country || null,
        stateID: state || null,
        city: data.city || '',
        categoryID: cat || null,
        subCategoryID: subCat || null,
        departmentID: department?.value || null,
        skillIds: link,
        employmentLength: data.employmentLength || '',
        description: description,
        terminationCondition: terminationCondition || '',
        ...(hasBudgetDuration
          ? { budget: Number(budget), duration: Number(duration) }
          : {}),
      };

      const apiCall = item
        ? apiPost(`${apiPath.manageEdit}/${item._id}`, payload)
        : apiPost(apiPath.manageAdd, payload);

      const res = await apiCall;

      if (res?.data?.success) {
        allJobs();
        if (fetchCompanyCompletionStatus) {
          fetchCompanyCompletionStatus();
        }
        handleCategory(false);
        notification.success(res?.data?.message);
      } else {
        notification.error(res?.data?.message);
      }
    } catch (err) {
      console.log('err:', err);
      notification.error('An error occurred. Please try again.');
    } finally {
      setIsSaving(false); // 🆕 stop spinner
    }
  };

  const onSubmit = (data) => {
    const bothSelected = budget && duration;

    if (!bothSelected) {
      submitJob(data);
      return;
    }

    const preview = calculateProRatedPreview(budget, duration);
    if (!preview) {
      submitJob(data);
      return;
    }

    const runningTotal =
      Math.round((Number(currentLedgerAmount || 0) + preview.amountThisCycle) * 100) / 100;

    const html = helpers.buildBillingChangeHtml({
      isNew: true,
      currentLedger: Number(currentLedgerAmount || 0),
      newBudget: Number(budget),
      newDuration: Number(duration),
      newThisCycle: preview.amountThisCycle,
      runningTotal,
    });

    helpers.confirmWithHtml(html, data, submitJob);
  };

  const getListWithoutPagination = async () => {
    try {
      const res = await apiGet(apiPath.listWithoutPagination, {});
      setCountryList(res?.data?.results?.country || []);
      setCityList(res?.data?.results?.city || []);
      setCategoryList(res?.data?.results?.category || []);
      setSubCategoryList(res?.data?.results?.subcategory || []);
      setDepartmentList(res?.data?.results?.department || []);
      setSkillList(res?.data?.results?.skills || []);
    } catch (error) {
      console.log('error:', error);
    }
  };

  const getStateList = async (countryId) => {
    try {
      const res = await apiGet(`${apiPath.getStateList}/${countryId}`, {});
      if (res?.data?.success && Array.isArray(res.data.results.allStates)) {
        setStateList(res.data.results.allStates);
      }
    } catch (error) {
      console.error('Error loading states:', error);
    }
  };

  // Fetch current subscription ledger on mount
useEffect(() => {
  const fetchLedger = async () => {
    try {
      setIsLoadingLedger(true);
      const res = await apiGet(apiPath.getBillingSummary);
      if (res?.data?.success) {
        const r = res.data.results || {};
        setCurrentLedgerAmount(Number(r.jobPostingAmount) || 0);
        setBillingCycle({
          start: r.billingCycleStart,
          end: r.billingCycleEnd,
        });
      }
    } catch (e) {
      console.error('Ledger fetch failed', e);
    } finally {
      setIsLoadingLedger(false);
    }
  };
  fetchLedger();
}, []);

  useEffect(() => {
    getListWithoutPagination();
  }, []);

  useEffect(() => {
    if (country) {
      getStateList(country);
    }
  }, [country]);

  useEffect(() => {
    if (department) {
      setSkill(
        skillList
          ?.filter(skillItem => skillItem?.departmentID?.toString() === department.value.toString())
          ?.map(data => ({
            label: data?.name,
            value: data?._id
          }))
      );
      setReferenceLink([]);
    }
  }, [department, skillList]);

  const departmentLinkOptions = departmentList.map(departmentItem => ({
    label: departmentItem?.name,
    value: departmentItem?._id
  }));

  const controlStyles = {
    padding: '9px 4px',
    borderRadius: '0.5rem',
    border: '2px solid rgb(223 223 223)'
  };

  const preventMaxHundredDays = (e) => {
    if (e.target.value > 30) {
      e.target.value = e.target.value.slice(0, 2);
    }
  };

  const isAIDisabled = (type) => {
    if (type === 'description') {
      return isGeneratingDescription || !jobTitle || jobTitle.trim() === '';
    }
    return isGeneratingTermination || !jobTitle || jobTitle.trim() === '';
  };

  // 🆕 Save button only requires description + job title (budget/duration OPTIONAL)
  const isSaveDisabled = !description || !jobTitle || isSaving;

  return (
    <>
      <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
        <div className="relative w-auto my-6 mx-auto max-h-[90%] overflow-y-auto">
          <div className="border-0 rounded-lg shadow-lg relative flex flex-col bg-white outline-none focus:outline-none w-[700px]">
            <div className="flex items-center justify-between p-5 border-b border-solid border-slate-200 rounded-t">
              <h3 className="text-xl font-semibold">
                {t('ADD_MANAGE_JOBS')}
              </h3>
              <button
                className="ml-auto flex items-center justify-center text-black border-2 rounded-full h-8 w-8 float-right text-3xl leading-none font-extralight outline-none focus:outline-none"
                onClick={() => handleCategory(false)}
                title="Close"
                disabled={isSaving}
              >
                <span className="text-[#B8BBBF] text-4xl" title="Close">
                  ×
                </span>
              </button>
            </div>

            <div className="relative p-6 flex-auto">
              <div className="grid sm:grid-cols-2">
                {/* Job Title */}
                <div className="md:py-4 sm:px-2 sm:py-8 px-7">
                  <div className="relative z-0 w-full group">
                    <OInputField
                      wrapperClassName="relative z-0 w-full group"
                      name="name"
                      inputLabel={
                        <>
                          {t('JOB_TITLE')}
                          <span className="text-red-500">*</span>
                        </>
                      }
                      type="text"
                      autoFocus
                      defaultValue={item?.name}
                      maxLength={100}
                      onInput={(e) => preventMaxInput(e, 100)}
                      register={register('name', {
                        required: 'Job title is required',
                        minLength: {
                          value: 2,
                          message: 'Job title must be at least 2 characters'
                        }
                      })}
                      errors={errors}
                    />
                    <p className="text-xs text-gray-500 mt-1">Required for AI generation</p>
                  </div>
                </div>

                {/* Country */}
                <div className="md:py-4 sm:px-2 sm:py-8 px-7">
                  <div className="relative z-0 w-full group">
                    <select
                      type="select"
                      name="country"
                      id="country"
                      className="block py-4 px-3 w-full text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 peer"
                      placeholder=" "
                      value={country || ''}
                      onChange={(e) => {
                        setCountry(e.target.value || null);
                        setState('');
                      }}
                    >
                      <option value={''}>
                        {t('SELECT_COUNTRY_NAME')}
                        <span className='text-red-500'>*</span>
                      </option>
                      {countryList.map((i, key) => (
                        <option key={key} value={i._id}>
                          {i.name}
                        </option>
                      ))}
                    </select>
                    <label
                      htmlFor="country"
                      className="dark:text-gray-400 duration-300 transform -translate-y-6 scale-75 top-3 peer-focus:font-normal absolute text-sm text-[#A5A5A5] left-3 bg-white p-2 z-10 origin-[2] peer-focus:left-0 peer-focus:text-[#A5A5A5] peer-focus:text-lg peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-8"
                    >
                      {t('O_COUNTRY')}
                      <span className='text-red-500'>*</span>
                    </label>
                  </div>
                </div>

                {/* State */}
                <div className="md:py-4 sm:px-2 sm:py-8 px-7">
                  <div className="relative z-0 w-full group">
                    <select
                      type="select"
                      name="state"
                      id="state"
                      className="block py-4 px-3 w-full text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 peer"
                      placeholder=" "
                      value={state || ''}
                      onChange={(e) => {
                        setState(e.target.value || null);
                      }}
                      disabled={!country}
                    >
                      <option value="">{t('SELECT_STATE_NAME')}</option>
                      {stateList.map((i, key) => (
                        <option key={key} value={i._id}>
                          {i.name}
                        </option>
                      ))}
                    </select>
                    <label
                      htmlFor="state"
                      className="dark:text-gray-400 duration-300 transform -translate-y-6 scale-75 top-3 peer-focus:font-normal absolute text-sm text-[#A5A5A5] left-3 bg-white p-2 z-10 origin-[2] peer-focus:left-0 peer-focus:text-[#A5A5A5] peer-focus:text-lg peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-8"
                    >
                      {t('O_STATE')}<span className='text-red-500'>*</span>
                    </label>
                  </div>
                </div>

                {/* City */}
                <div className="md:py-4 sm:px-2 sm:py-8 px-7">
                  <div className="relative z-0 w-full group">
                    <OInputField
                      wrapperClassName="relative z-0 w-full group"
                      name="city"
                      inputLabel={
                          <>
                            {t('CITY')}
                            <span className='text-red-500'>*</span>
                          </>
                        }
                      type="text"
                      autoFocus
                      defaultValue={item?.city}
                      maxLength={100}
                      onInput={(e) => preventMaxInput(e, 100)}
                      register={register('city')}
                      errors={errors}
                    />
                  </div>
                </div>

                {/* Category */}
                <div className="md:py-4 sm:px-2 sm:py-8 px-7">
                  <div className="relative z-0 w-full group">
                    <select
                      type="select"
                      name="category"
                      id="category"
                      className="block py-4 px-3 w-full text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 peer"
                      placeholder=" "
                      value={cat || ''}
                      onChange={(e) => {
                        setCat(e.target.value || null);
                        setSubCat('');
                      }}
                    >
                      <option value={''}>
                          {t('SELECT_CATEGORY')}
                          <span className='text-red-500'>*</span>
                        </option>
                      {categoryList.map((i, index) => (
                        <option key={index} value={i._id}>
                          {i.name}
                        </option>
                      ))}
                    </select>
                    <label
                      htmlFor="category"
                      className="peer-focus:font-normal absolute text-sm text-[#A5A5A5] dark:text-gray-400 duration-300 transform -translate-y-6 scale-75 top-3 left-3 bg-white p-2 z-10 origin-[2] peer-focus:left-0 peer-focus:text-[#A5A5A5] peer-focus:text-lg peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-8"
                    >
                      {t('CATEGORY')}<span className='text-red-500'>*</span>
                    </label>
                  </div>
                </div>

                {/* Subcategory */}
                <div className="md:py-4 sm:px-2 sm:py-8 px-7">
                  <div className="relative z-0 w-full group">
                    <select
                      type="select"
                      name="categoryId"
                      id="categoryId"
                      className="block py-4 px-3 w-full text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 peer"
                      placeholder=" "
                      value={subCat || ''}
                      onChange={(e) => {
                        setSubCat(e.target.value || null);
                      }}
                      disabled={!cat}
                    >
                      <option value="">{t('SELECT_SUBCATEGORY')}</option>
                      {subCategoryList.map(
                        (i, key) =>
                          i?.categoryId == cat && (
                            <option key={key} value={i?._id}>
                              {i?.name}
                            </option>
                          )
                      )}
                    </select>
                    <label
                      htmlFor="categoryId"
                      className="peer-focus:font-normal absolute text-sm text-[#A5A5A5] dark:text-gray-400 duration-300 transform -translate-y-6 scale-75 top-3 left-3 bg-white p-2 z-10 origin-[2] peer-focus:left-0 peer-focus:text-[#A5A5A5] peer-focus:text-lg peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-8"
                    >
                      {t('SUBCATEGORY_NAME')}<span className='text-red-500'>*</span>
                    </label>
                  </div>
                </div>

                {/* Department */}
                <div className="md:py-4 sm:px-2 sm:py-8 px-7">
                  <div className="relative z-11 w-full group">
                    <Select
                      styles={{
                        control: (baseStyles) => ({
                          ...baseStyles,
                          ...controlStyles
                        })
                      }}
                      name="department"
                      options={departmentLinkOptions}
                      placeholder={
                          <>
                            {t('SELECT_DEPARTMENT')}
                            <span className='text-red-500'>*</span>
                          </>
                        }
                      className="basic-multi-select"
                      classNamePrefix="select"
                      value={department}
                      onChange={(items) => {
                        setDepartment(items);
                      }}
                      isClearable={true}
                    />
                  </div>
                </div>

                {/* Skills */}
                <div className="md:py-4 sm:px-2 sm:py-8 px-7">
                  <div className="relative z-11 w-full group">
                    <Select
                      isMulti
                      styles={{
                        control: (baseStyles) => ({
                          ...baseStyles,
                          ...controlStyles
                        })
                      }}
                      name="referenceLink"
                      options={skill}
                      placeholder={t('SELECT_SKILLS')}
                      className="basic-multi-select"
                      classNamePrefix="select"
                      value={referenceLink}
                      onChange={(itemData) => {
                        setReferenceLink(itemData);
                      }}
                      isDisabled={!department}
                    />
                  </div>
                </div>

                {/* Employment Length */}
                <div className="md:py-4 sm:px-2 sm:py-8 px-7">
                  <div className="relative z-0 w-full group">
                    <OInputField
                      wrapperClassName="relative z-0 w-full group"
                      errors={errors}
                      inputLabel={
                        <>
                            {t('EMPLOYEEMENT_LENGTH')} (In days)
                            <span className='text-red-500'>*</span>
                          </>
                      }
                      type="text"
                      name="employmentLength"
                      defaultValue={item?.employmentLength}
                      onInput={(e) => preventMaxHundredDays(e)}
                      maxLength={2}
                      onKeyDown={(event) => {
                        if (
                          !['Backspace', 'Delete', 'Tab'].includes(event.key) &&
                          !/[0-9]/.test(event.key)
                        ) {
                          event.preventDefault();
                        }
                      }}
                      id="employmentLength"
                      register={register('employmentLength', {
                        pattern: {
                          value: /^[0-9]*$/,
                          message: 'Only digits are allowed.'
                        },
                        max: {
                          value: 30,
                          message: 'Maximum limit is 30 days.'
                        }
                      })}
                    />
                  </div>
                </div>

                {/* Budget — OPTIONAL */}
                <div className="md:py-4 sm:px-2 sm:py-8 px-7">
                  <div className="relative z-0 w-full group">
                    <select
                      name="budget"
                      id="budget"
                      className="block py-4 px-3 w-full text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 peer"
                      value={budget}
                      onChange={(e) => setBudget(e.target.value)}
                    >
                      <option value="">{t('SELECT_BUDGET') || 'Select budget (optional)'}</option>
                      {BUDGET_OPTIONS.map((b) => (
                        <option key={b} value={b}>${b}</option>
                      ))}
                    </select>
                    <label
                      htmlFor="budget"
                      className="peer-focus:font-normal absolute text-sm text-[#A5A5A5] dark:text-gray-400 duration-300 transform -translate-y-6 scale-75 top-3 left-3 bg-white p-2 z-10 origin-[2] peer-focus:left-0 peer-focus:text-[#A5A5A5] peer-focus:text-lg peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-8"
                    >
                      {t('BUDGET') || 'Budget'} <span className="text-xs text-gray-400">(optional)</span>
                    </label>
                  </div>
                </div>

                {/* Duration — OPTIONAL */}
                <div className="md:py-4 sm:px-2 sm:py-8 px-7">
                  <div className="relative z-0 w-full group">
                    <select
                      name="duration"
                      id="duration"
                      className="block py-4 px-3 w-full text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 peer"
                      value={duration}
                      onChange={(e) => setDuration(e.target.value)}
                    >
                      <option value="">{t('SELECT_DURATION') || 'Select duration (optional)'}</option>
                      {DURATION_OPTIONS.map((d) => (
                        <option key={d} value={d}>{d} days</option>
                      ))}
                    </select>
                    <label
                      htmlFor="duration"
                      className="peer-focus:font-normal absolute text-sm text-[#A5A5A5] dark:text-gray-400 duration-300 transform -translate-y-6 scale-75 top-3 left-3 bg-white p-2 z-10 origin-[2] peer-focus:left-0 peer-focus:text-[#A5A5A5] peer-focus:text-lg peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-8"
                    >
                      {t('DURATION') || 'Duration'} <span className="text-xs text-gray-400">(optional)</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Job Description */}
              <div className="grid">
                <div className="md:py-4 sm:px-2 sm:py-8 px-7">
                  <div className="relative z-0 w-full group">
                    <div className="flex justify-between items-center mb-2">
                      <label className='text-sm text-[#A5A5A5] font-medium'>
                        {t('JOB_DESCRIPTION')}
                        <span className="text-red-500">*</span>
                      </label>

                      <button
                        type="button"
                        onClick={() => generateWithAI('description')}
                        disabled={isAIDisabled('description')}
                        className={`flex items-center gap-2 px-4 py-2 text-sm rounded-lg transition-colors ${
                          isAIDisabled('description')
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                        }`}
                        title="Generate professional job description with AI"
                      >
                        {isGeneratingDescription ? (
                          <>
                            <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Generating...
                          </>
                        ) : (
                          <>
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                            Generate with AI
                          </>
                        )}
                      </button>
                    </div>

                    <div className="border-2 border-[#DFDFDF] rounded-lg overflow-hidden">
                      <Editor
                        apiKey={process.env.REACT_APP_TINY_MCE_KEY}
                        value={description}
                        onEditorChange={(content) => {
                          setDescription(content);
                        }}
                        init={{
                          plugins: [
                            "lists",
                            "link",
                            "image",
                            "charmap",
                            "preview",
                            "anchor",
                            "searchreplace",
                            "visualblocks",
                            "code",
                            "fullscreen",
                            "insertdatetime",
                            "media",
                            "table",
                            "help",
                            "wordcount"
                          ],
                          toolbar: "undo redo | formatselect | " +
                                  "bold italic underline strikethrough | " +
                                  "alignleft aligncenter alignright alignjustify | " +
                                  "bullist numlist outdent indent | " +
                                  "link image | " +
                                  "removeformat | help",
                          height: 300,
                          menubar: false,
                          statusbar: true,
                          branding: false,
                          content_style: 'body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; font-size: 14px; }',
                          setup: (editor) => {
                            editor.on('change', () => {
                              setDescription(editor.getContent());
                            });
                          }
                        }}
                      />
                    </div>

                    {!description && (
                      <p className="text-red-500 text-sm mt-1">Job description is required</p>
                    )}
                  </div>
                </div>

                {/* Termination Condition */}
                <div className="md:py-4 sm:px-2 sm:py-8 px-7">
                  <div className="relative z-0 w-full group">
                    <div className="flex justify-between items-center mb-2">
                      <label className='text-sm text-[#A5A5A5] font-medium'>
                        {t('TERMINATION_CONDITION')}
                      </label>

                      <button
                        type="button"
                        onClick={() => generateWithAI('termination')}
                        disabled={isAIDisabled('termination')}
                        className={`flex items-center gap-2 px-4 py-2 text-sm rounded-lg transition-colors ${
                          isAIDisabled('termination')
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                        }`}
                        title="Generate professional termination conditions with AI"
                      >
                        {isGeneratingTermination ? (
                          <>
                            <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Generating...
                          </>
                        ) : (
                          <>
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                            Generate with AI
                          </>
                        )}
                      </button>
                    </div>

                    <div className="border-2 border-[#DFDFDF] rounded-lg overflow-hidden">
                      <Editor
                        apiKey={process.env.REACT_APP_TINY_MCE_KEY}
                        value={terminationCondition}
                        onEditorChange={(content) => {
                          setTerminationCondition(content);
                        }}
                        init={{
                          plugins: [
                            "lists",
                            "link",
                            "image",
                            "charmap",
                            "preview",
                            "anchor",
                            "searchreplace",
                            "visualblocks",
                            "code",
                            "fullscreen",
                            "insertdatetime",
                            "media",
                            "table",
                            "help",
                            "wordcount"
                          ],
                          toolbar: "undo redo | formatselect | " +
                                  "bold italic underline strikethrough | " +
                                  "alignleft aligncenter alignright alignjustify | " +
                                  "bullist numlist outdent indent | " +
                                  "link image | " +
                                  "removeformat | help",
                          height: 300,
                          menubar: false,
                          statusbar: true,
                          branding: false,
                          content_style: 'body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; font-size: 14px; }',
                          setup: (editor) => {
                            editor.on('change', () => {
                              setTerminationCondition(editor.getContent());
                            });
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Form Buttons */}
            <div className="flex items-center justify-center p-6 border-t border-solid border-slate-200 rounded-b">
              <button
                className="text-black bg-[#E1E1E1] font-normal px-12 py-2.5 text-sm outline-none focus:outline-none rounded mr-6 ease-linear transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed"
                type="button"
                onClick={() => handleCategory(false)}
                disabled={isSaving}
              >
                {t('O_BACK')}
              </button>
              <button
                className={`font-normal text-sm px-8 py-2.5 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 ease-linear transition-all duration-150 inline-flex items-center gap-2 ${
                  isSaveDisabled
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-LightBlue text-white hover:bg-blue-600'
                }`}
                type="button"
                onClick={handleSubmit(onSubmit)}
                // disabled={isSaveDisabled}
              >
                {isSaving ? (
                  <>
                    <svg
                      className="animate-spin h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    {t('SAVING') || 'Saving...'}
                  </>
                ) : (
                  t('O_ADD')
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="opacity-25 fixed inset-0 z-40 bg-black" />
    </>
  );
};

export default ManageAdd;